import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { Button } from '@/components/ui/button';
import { useUserStore } from '@/stores/user';
import { HiAttemptStep1OwnerInfo } from '@/pages/homeInspection/steps/HiAttemptStep1OwnerInfo';
import { HiAttemptStep2TenantInfo } from '@/pages/homeInspection/steps/HiAttemptStep2TenantInfo';
import { HiAttemptStep3PropertyInfo, type PreviewEntry } from '@/pages/homeInspection/steps/HiAttemptStep3PropertyInfo';
import { HiAttemptStep4Comments } from '@/pages/homeInspection/steps/HiAttemptStep4Comments';
import { buildAttemptImagePreviews } from '@/pages/homeInspection/hiAttemptHelpers';
import type { WorkOrderAttemptListResponse } from '@/types/workOrderAttempt.types';
import { canViewPsAttempt } from './canViewPsAttempt';
import { canCreatePsAttempt } from './canCreatePsAttempt';
import {
  getPsFirstAttemptHiSteps,
  getPsFirstAttemptHiViewHeader,
  resolvePsCaseTypeId,
} from './psFirstAttemptHiConfig';
import type { PsFirstAttemptHiFormValues } from './psFirstAttemptHi.schema';
import { mapPsFirstAttemptHiViewDefaults } from './mapPsFirstAttemptHiViewDefaults';
import { adaptPsHiAttemptToHiGetResponse } from './adaptPsHiAttemptToHiGetResponse';
import { PsFirstAttemptServiceInfoStep } from './steps/PsFirstAttemptServiceInfoStep';
import { PsHiVacancyChecklistStep } from './steps/PsHiVacancyChecklistStep';

const noopSetPreviews: Dispatch<SetStateAction<PreviewEntry[]>> = () => undefined;

/** View mode: accept all values; do not run create/update Zod validation (REQ-5). */
const viewModeResolver = (values: PsFirstAttemptHiFormValues) =>
  Promise.resolve({
    values,
    errors: {},
  });

type WorkOrderStub = {
  createdAt?: string | Date;
  server?: { firstName: string; lastName: string } | null;
  service?: {
    case?: {
      typeId?: string | null;
      type?: { caseTypeId?: string | null; name?: string | null } | null;
    } | null;
  } | null;
};

type Props = {
  caseTypeId: string | null | undefined;
  workOrderNumber: string;
  /** Preloaded from parent — avoids a second attempt GET. */
  attemptList: WorkOrderAttemptListResponse;
  workOrder: WorkOrderStub;
};

export const PsFirstAttemptHiView = ({
  caseTypeId,
  workOrderNumber,
  attemptList,
  workOrder,
}: Props) => {
  const { id: workOrderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const loggedInUser = useUserStore((state) => state.user);

  const resolvedCaseTypeId = useMemo(
    () => resolvePsCaseTypeId(workOrder.service?.case) ?? caseTypeId ?? null,
    [workOrder.service?.case, caseTypeId]
  );

  const stepConfigs = useMemo(
    () => getPsFirstAttemptHiSteps({ caseTypeId: resolvedCaseTypeId }),
    [resolvedCaseTypeId]
  );
  const header = getPsFirstAttemptHiViewHeader(workOrderNumber);

  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(() =>
    stepConfigs.map((s) => ({ title: s.title, isCompleted: true }))
  );

  const psAttempt = attemptList.workOrderAttempts[0];
  const hiAttempt = attemptList.homeInspectionAttempts?.[0];
  const missingHiAttempt =
    attemptList.homeInspectionAttempts === undefined || hiAttempt === undefined;

  const form = useForm<PsFirstAttemptHiFormValues>({
    resolver: viewModeResolver,
    mode: 'all',
    defaultValues: {
      serviceAttempt: { images: [], serverName: '' },
      tenants: [],
      mediaIds: [],
      vacancyChecklist: {},
    },
  });

  const hiImagePreviews = useMemo(() => {
    if (!hiAttempt) return [];
    return buildAttemptImagePreviews(adaptPsHiAttemptToHiGetResponse(hiAttempt));
  }, [hiAttempt]);

  useEffect(() => {
    setSteps(stepConfigs.map((s) => ({ title: s.title, isCompleted: true })));
    setActiveStep(0);
  }, [stepConfigs]);

  useEffect(() => {
    if (!psAttempt || !hiAttempt) return;
    form.reset(
      mapPsFirstAttemptHiViewDefaults({
        listResponse: attemptList,
        psAttempt,
        hiAttempt,
      })
    );
  }, [attemptList, psAttempt, hiAttempt, form]);

  if (!canViewPsAttempt(loggedInUser)) {
    return <Navigate to="/not-found" />;
  }

  if (!psAttempt || missingHiAttempt) {
    return (
      <div className="flex items-center justify-center h-64 text-error text-sm">
        Failed to load attempt. Please go back and try again.
      </div>
    );
  }

  const stepId = stepConfigs[activeStep]?.id;
  const isLastStep = activeStep === stepConfigs.length - 1;

  const exitToProcess = () => navigate(`/process?workOrderId=${workOrderId}`);

  const scrollToTop = () => {
    const el = document.getElementById('toolbar');
    if (el) el.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    scrollToTop();
  };

  const nextStep = () => {
    setActiveStep((prev) => Math.min(prev + 1, stepConfigs.length - 1));
    scrollToTop();
  };

  const goToStep = (step: number) => {
    setActiveStep(step);
    scrollToTop();
  };

  const hiWorkOrderStub = {
    createdAt: workOrder.createdAt ? String(workOrder.createdAt) : new Date().toISOString(),
    server: workOrder.server
      ? { firstName: workOrder.server.firstName, lastName: workOrder.server.lastName }
      : null,
  };

  const canEdit = canCreatePsAttempt(loggedInUser);

  return (
    <div className="w-full scrollbar-hide flex flex-col">
      <div className="w-full flex flex-row justify-between">
        <ToolBar
          title={header.title}
          secondaryTitle={<p className="text-sm text-placeholder">{header.subtitle}</p>}
        />
        {canEdit && (
          <Button
            className="h-[36px] p-3 w-fit"
            onClick={() => {
              const params = new URLSearchParams();
              params.set('mode', 'edit');
              if (psAttempt.attemptId) params.set('attemptId', psAttempt.attemptId);
              navigate(`?${params.toString()}`, { replace: true });
            }}
          >
            Edit Attempt
          </Button>
        )}
      </div>

      <div className="mt-3 flex justify-center overflow-x-auto pl-24">
        <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} />
      </div>

      <FormProvider {...form}>
        {stepId === 'serviceInformation' && <PsFirstAttemptServiceInfoStep readOnly />}
        {stepId === 'ownerInformation' && (
          <HiAttemptStep1OwnerInfo workOrder={hiWorkOrderStub} hideDateTime readOnly />
        )}
        {stepId === 'tenantInformation' && <HiAttemptStep2TenantInfo readOnly />}
        {stepId === 'propertyInformation' && (
          <div className="flex flex-col gap-6">
            <HiAttemptStep3PropertyInfo
              previews={hiImagePreviews}
              setPreviews={noopSetPreviews}
              readOnly
            />
            <HiAttemptStep4Comments workOrder={hiWorkOrderStub} readOnly />
          </div>
        )}
        {stepId === 'vacancyChecklist' && <PsHiVacancyChecklistStep readOnly />}
      </FormProvider>

      <div className="flex justify-end mt-4 mb-8 w-full gap-2">
        <Button variant="outline" className="w-[125px] h-[48px]" onClick={exitToProcess}>
          Back
        </Button>
        {activeStep > 0 && (
          <Button
            variant="outline"
            className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
            onClick={prevStep}
          >
            Previous
          </Button>
        )}
        {!isLastStep && (
          <Button
            className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white"
            onClick={nextStep}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
};
